import { map } from 'lodash';

import moment from 'moment';
import { AttendeesList, IAttendeesListData } from './attendeesList-model';

const MEETING_TYPE_GEN_ED: number = 1;

export interface IScheduleMeetingData {
  meetingSk?: string;
  parentSk?: string;
  personSk?: string;
  studentSk?: string;
  nycId?: string;
  meetingDate?: Date;
  meetingStartTime?: Date;
  meetingEndTime?: Date;
  meetingStreetAddress1?: string;
  meetingFloorRoom?: string;
  meetingType?: number;
  parentReqReschedule?: boolean;
  student?: IStudentData;
  parent?: IParentData;
  generalEducationTeacher?: IGeneralEducationTeacherData;
  specialEducationTeacher?: ISpecialEducationTeacherData;
  schoolSocialWorker?: ISchoolSocialWorkerData;
  schoolPsychologist?: ISchoolPsychologistData;
  relatedServiceProvider?: IRelatedServiceProviderData;
  districtRepresentative?: IDistrictRepresentativeData;
  principal?: IPrincipalData;
  teacher?: ITeacherData;
  attendee?: IAttendeesListData[];
  subMeetingType?: number;
  additionalInfo?: string;
  requiredAttendees?: [];
  optionalAttendees?: [];
  assessmentsList?: [];
  evaluationSk?: string;
}

export interface IStudentData {
  studentName?: string;
  studentEmail?: string;
}

export interface IParentData {
  parentName?: string;
  parentEmail?: string;
  isAttendeeRequired?: boolean;
}

export interface IGeneralEducationTeacherData {
  generalEducationTeacherName?: string;
  generalEducationTeacherEmail?: string;
  generalEducationTeacherPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface ISpecialEducationTeacherData {
  specialEducationTeacherName?: string;
  specialEducationTeacherEmail?: string;
  specialEducationTeacherPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface ISchoolSocialWorkerData {
  schoolSocialWorkerName?: string;
  schoolSocialWorkerEmail?: string;
  schoolSocialWorkerPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface ISchoolPsychologistData {
  schoolPsychologistName?: string;
  schoolPsychologistEmail?: string;
  schoolPsychologistPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface IRelatedServiceProviderData {
  relatedServiceProviderName?: string;
  relatedServiceProviderEmail?: string;
  relatedServiceProviderPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface IDistrictRepresentativeData {
  districtRepresentativerName?: string;
  districtRepresentativerEmail?: string;
  districtRepresentativerPhoneNumber?: string;
  isAttendeeRequired?: boolean;
}

export interface IPrincipalData {
  principalName?: string;
  principalEmail?: string;
}

export interface ITeacherData {
  teacherName?: string;
  teacherEmail?: string;
}

export interface IAssessmentData {
  AssessmentSk?: string;
  assessmentSk?: string;
}

export class SubmitScheduleMeetingModel {
  parentSk: string;
  personSk: string;
  meetingSk: string;
  studentSk: string;
  nycId: string;
  meetingType: string;
  meetingEndTime: Date;
  actualoccurDate: string;
  origScheduleDt: string;
  currScheduleDt: string;
  parentReqReschedule: boolean;
  parentConfirm: boolean;
  formJson: string;
  location: string;
  student: Student;
  parent: Parent;
  generalEducationTeacher: GeneralEducationTeacher;
  schoolSocialWorker: SchoolSocialWorker;
  schoolPsychologist: SchoolPsychologist;
  relatedServiceProvider: RelatedServiceProvider;
  districtRepresentative: DistrictRepresentative;
  principal: Principal;
  teacher: Teacher;
  specialEducationTeacher: SpecialEducationTeacher;
  attend: AttendeesList[];
  subMeetingType: number;
  assessments: Assessment[];
  additionalInfo: string;
  requiredAttendees: [];
  optionalAttendees: [];
  assessmentsList: [];
  evaluationSk: string;
  shmMinDate: Date;
  pwnNoticeDate: Date;
  constructor(data: Partial<IScheduleMeetingData>) {
    const combinedMeetingDateTime: string = moment(data?.meetingDate)
      .set({
        hour: moment(data?.meetingStartTime).hours(),
        minute: moment(data?.meetingStartTime).minutes(),
      })
      .format('MM/DD/YYYY hh:mm A');
    this.meetingSk = data.meetingSk;
    this.parentSk = data.parentSk;
    this.personSk = data.personSk;
    this.studentSk = data.studentSk;
    this.nycId = data.nycId;
    this.actualoccurDate =
      moment(combinedMeetingDateTime, 'MM/DD/YYYY hh:mm A').local().format('YYYY-MM-DDTHH:mm:ss') +
      'Z';
    this.currScheduleDt =
      moment(combinedMeetingDateTime, 'MM/DD/YYYY hh:mm A').local().format('YYYY-MM-DDTHH:mm:ss') +
      'Z';
    this.origScheduleDt =
      moment(combinedMeetingDateTime, 'MM/DD/YYYY hh:mm A').local().format('YYYY-MM-DDTHH:mm:ss') +
      'Z';
    this.location = `${data?.meetingStreetAddress1}${
      data?.meetingFloorRoom ? ', ' + data.meetingFloorRoom : ''
    }`;
    this.meetingType =
      data.meetingType === 0 ? 'SHM' : data.meetingType === MEETING_TYPE_GEN_ED ? 'GenEd' : 'IEP';
    this.meetingEndTime = data?.meetingEndTime;
    this.parentReqReschedule = data?.parentReqReschedule;
    this.parentConfirm = true;
    this.student = new Student(data?.student);
    this.parent = new Parent(data?.parent);
    this.student = new Student(data?.student);
    this.generalEducationTeacher = new GeneralEducationTeacher(
      data?.generalEducationTeacher
    );
    this.specialEducationTeacher = new SpecialEducationTeacher(
      data?.specialEducationTeacher
    );
    this.schoolSocialWorker = new SchoolSocialWorker(data?.schoolSocialWorker);
    this.schoolPsychologist = new SchoolPsychologist(data?.schoolPsychologist);
    this.relatedServiceProvider = new RelatedServiceProvider(
      data?.relatedServiceProvider
    );
    this.districtRepresentative = new DistrictRepresentative(
      data?.districtRepresentative
    );
    this.principal = new Principal(data?.principal);
    this.teacher = new Teacher(data?.teacher);
    this.attend = map(
      data?.attendee,
      (row: IAttendeesListData) => new AttendeesList(row)
    );
    this.subMeetingType = data.subMeetingType;
    this.additionalInfo = data?.additionalInfo;
    this.requiredAttendees = data?.requiredAttendees;
    this.optionalAttendees = data?.optionalAttendees;
    this.assessmentsList = data?.assessmentsList;
    this.evaluationSk = data?.evaluationSk;
  }

  get id(): string {
    return this.parentSk;
  }
}

export class Student {
  studentName: string;
  studentEmail: string;
  constructor(data: Partial<IStudentData>) {
    this.studentName = data?.studentName;
    this.studentEmail = data?.studentEmail;
  }
}

export class Assessment {
  AssessmentSk?: string;
  assessmentSk?: string;
  constructor(data: Partial<IAssessmentData>) {
    this.AssessmentSk = data?.AssessmentSk;
    this.assessmentSk = data?.assessmentSk;
  }
}

export class Parent {
  parentName: string;
  parentEmail: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<IParentData>) {
    this.parentName = data?.parentName;
    this.parentEmail = data?.parentEmail;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class GeneralEducationTeacher {
  generalEducationTeacherName: string;
  generalEducationTeacherEmail: string;
  generalEducationTeacherPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<IGeneralEducationTeacherData>) {
    this.generalEducationTeacherName = data?.generalEducationTeacherName;
    this.generalEducationTeacherEmail = data?.generalEducationTeacherEmail;
    this.generalEducationTeacherPhoneNumber =
      data?.generalEducationTeacherPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class SpecialEducationTeacher {
  specialEducationTeacherName: string;
  specialEducationTeacherEmail: string;
  specialEducationTeacherPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<ISpecialEducationTeacherData>) {
    this.specialEducationTeacherName = data?.specialEducationTeacherName;
    this.specialEducationTeacherEmail = data?.specialEducationTeacherEmail;
    this.specialEducationTeacherPhoneNumber =
      data?.specialEducationTeacherPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class SchoolSocialWorker {
  schoolSocialWorkerName: string;
  schoolSocialWorkerEmail: string;
  schoolSocialWorkerPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<ISchoolSocialWorkerData>) {
    this.schoolSocialWorkerName = data?.schoolSocialWorkerName;
    this.schoolSocialWorkerEmail = data?.schoolSocialWorkerEmail;
    this.schoolSocialWorkerPhoneNumber = data?.schoolSocialWorkerPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class SchoolPsychologist {
  schoolPsychologistName: string;
  schoolPsychologistEmail: string;
  schoolPsychologistPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<ISchoolPsychologistData>) {
    this.schoolPsychologistName = data?.schoolPsychologistName;
    this.schoolPsychologistEmail = data?.schoolPsychologistEmail;
    this.schoolPsychologistPhoneNumber = data?.schoolPsychologistPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}
export class RelatedServiceProvider {
  relatedServiceProviderName: string;
  relatedServiceProviderEmail: string;
  relatedServiceProviderPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<IRelatedServiceProviderData>) {
    this.relatedServiceProviderName = data?.relatedServiceProviderName;
    this.relatedServiceProviderEmail = data?.relatedServiceProviderEmail;
    this.relatedServiceProviderPhoneNumber =
      data?.relatedServiceProviderPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class DistrictRepresentative {
  districtRepresentativerName: string;
  districtRepresentativerEmail: string;
  districtRepresentativerPhoneNumber: string;
  isAttendeeRequired: boolean;
  constructor(data: Partial<IDistrictRepresentativeData>) {
    this.districtRepresentativerName = data?.districtRepresentativerName;
    this.districtRepresentativerEmail = data?.districtRepresentativerEmail;
    this.districtRepresentativerPhoneNumber =
      data?.districtRepresentativerPhoneNumber;
    this.isAttendeeRequired = data?.isAttendeeRequired;
  }
}

export class Principal {
  principalName: string;
  principalEmail: string;
  constructor(data: Partial<IPrincipalData>) {
    this.principalName = data?.principalName;
    this.principalEmail = data?.principalEmail;
  }
}

export class Teacher {
  teacherName: string;
  teacherEmail: string;
  constructor(data: Partial<ITeacherData>) {
    this.teacherName = data?.teacherName;
    this.teacherEmail = data?.teacherEmail;
  }
}
